import { Contact, IntervalConfig, InspectionItem, ProjectSection, Report, ReportMetadata, ServiceTransaction } from '../types';
import { calculateNextDueDate, parseDateLocal } from './dateService';
import { istBuchungDerTochter, istKindBuchung } from './linkedBillingMode';
import { istSifaProjekt } from './projectType';
import { SendGuardDecision } from './sendGuard';
import { isAccountingEntry, reportNumberOf } from './serviceLogUtils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ENTWURF VERWERFEN — der Rueckweg von „Naechste Begehung anlegen"
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * „Naechste Begehung anlegen" (useReportActions.handleReportAction, Modus
 * 'next') archiviert den laufenden Bericht #N als Snapshot und macht aus dem
 * lebenden Bericht den Entwurf #N+1. Dabei kippt eine ganze Reihe von
 * Zaehlern und Feldern:
 *
 *   - metadata.inspectionNumber            N → N+1
 *   - projects.current_inspection_number   N → N+1 (Direkt-Update)
 *   - sections[S].currentInspectionNumber  +1, lastDate = neues Datum
 *   - metadata.date / time                 → Datum und Uhrzeit des Folgeberichts
 *   - projects.last_inspection_date        → neues Datum
 *   - intervalConfig.nextScheduledDate     → aus dem neuen Datum gerechnet
 *   - projects.next_scheduled_date         → dito
 *   - metadata.isExported                  → false
 *   - currentBillingSplit                  → weg, currentBillingServiceId gesetzt
 *   - distributionList                     → Anwesenheiten geloescht
 *   - guests                               → []
 *   - items                                'good' → 'closed' (mit closedDate)
 *
 * Bleibt so ein Entwurf leer stehen, ist er trotzdem eine Nummer: der
 * Nachtragsdialog uebernimmt den Berichtszaehler in quantity_used, der
 * Sammelnachtrag datiert die Menge, und ein verspaeteter Versand bucht auf
 * das geerbte Datum (Vorfaelle 6214, 6211). Deshalb muss ein versehentlich
 * angelegter Entwurf wieder weg — und zwar so, dass ALLE Zaehler auf dem
 * Stand von vorher sind, nicht nur die Nummer.
 *
 * ─── Quelle des alten Stands ────────────────────────────────────────────────
 * Der Archiv-Snapshot von #N (report_history.full_report_json) ist ein
 * Vollbackup des Berichts zum Zeitpunkt des Anlegens — genau der Stand, der
 * wiederhergestellt werden soll. Aus ihm kommen die BERICHTSfelder (Items,
 * Checklisten, Datum, Nummer, Anwesenheit, Abrechnungswahl). Was NICHT aus
 * dem Snapshot kommt, ist alles Projektweite: Positionen, Protokoll, Notizen,
 * Kontakte, Vorankuendigung, Unterlage, Gefaehrdungsmatrix, Sicherheitspunkte,
 * Einstellungen. Das hat sich seit dem Anlegen womoeglich weiterbewegt
 * (Buchungen, Kontaktpflege, andere Module), und ein Vollblob aus dem Archiv
 * wuerde es zuruecksetzen — dieselbe Fehlerklasse wie „Vollblob verliert
 * Buchungen". Dass der Snapshot von #N-1 dort andere Staende traegt als der
 * wiederhergestellte Bericht, ist deshalb gewollt; der Anlegepfad schreibt
 * diese Felder ebenfalls einfach fort.
 *
 * Vor der ersten Begehung gibt es keinen Snapshot (isArchivableReport). Dann
 * ist der alte Stand die leere Huelle aus dem Anlagedialog: Nummer 0, kein
 * Datum, keine Eintraege, keine Checklisten.
 *
 * ─── Wann NICHT ────────────────────────────────────────────────────────────
 * Ein Bericht, der versendet, archiviert oder gebucht ist, ist kein Entwurf
 * mehr; einer aus einer ungeprueften Freelancer-Begehung traegt fremde
 * Feldarbeit. Jede dieser Spuren blockiert, und eine gescheiterte Pruefung
 * blockiert ebenfalls: fuer einen loeschenden Vorgang gilt fail-closed, nicht
 * fail-open wie beim Versand-Riegel.
 */

export type DraftDiscardBlockReason =
    | 'no_draft'
    | 'exported'
    | 'archived'
    | 'booked'
    | 'booked_parent'
    | 'freelancer'
    | 'no_snapshot'
    | 'ambiguous_snapshot'
    | 'lookup_failed';

export interface DraftDiscardEvidence {
    /** projects.last_exported_inspection_number, null wenn unbekannt. */
    dbLastExportedNr: number | null;
    /** Archiv-Zeilen mit der Nummer des ENTWURFS (jede blockiert, Abschnitt egal). */
    draftArchiveRows: Array<{ inspectionNumber: number; sectionId: string | null }>;
    /**
     * Urteil des Versand-Riegels (sendGuard.evaluateSendGuard) fuer die
     * Nummer des Entwurfs — mit dessen Altersfilter fuer Sendeberichte, den
     * ein nackter Betreff-Abgleich nicht haette (Vorfall 6214: Sendeberichte
     * einer anderen Baustelle im ueberschriebenen Projekt).
     */
    sendDecision: Pick<SendGuardDecision, 'alreadySent' | 'reason'> | null;
    /** Snapshot des vorigen Berichts #N-1, null wenn es keinen gibt. */
    previousSnapshot: Report | null;
    /**
     * Zu #N-1 liegen MEHRERE Archivzeilen (verschiedene Abschnitte — der
     * Eindeutigkeitsindex ist project, nummer, section). Welche davon der
     * Stand unmittelbar vor dem Anlegen war, laesst sich ohne Zeitstempel
     * nicht sagen (save_report_snapshot fuehrt keinen nach) — dann wird
     * nicht geraten, sondern blockiert.
     */
    previousSnapshotAmbiguous?: boolean;
    /** Protokoll des Mutterprojekts, wenn dieses Projekt eine Tochter ist. */
    parentServiceLog: ServiceTransaction[] | null;
    /**
     * Freigabe-Riegel (reportReleaseGuard): stammt der Bericht aus einer
     * Freelancer-Begehung, die noch nicht geprueft ist, tragen seine Eintraege
     * fremde Feldarbeit — die wird nicht vom Innendienst verworfen, sondern
     * geprueft oder zur Nachbesserung zurueckgegeben. null = nicht gefragt.
     */
    freelancerRelease: { blockiert: boolean; hinweis: string | null } | null;
    /** Irgendeine Abfrage ist gescheitert — dann wird nicht verworfen. */
    lookupFailed?: boolean;
}

export interface DraftDiscardColumns {
    current_inspection_number: number;
    last_inspection_date: string | null;
    next_scheduled_date: string | null;
}

export interface DraftDiscardPlanOk {
    ok: true;
    draftNr: number;
    previousNr: number;
    /** Der Bericht, der nach dem Verwerfen der laufende ist. */
    restored: Report;
    /** Fruehe Vorgriffe auf die projects-Spalten (ohne updated_at!). */
    columns: DraftDiscardColumns;
    /**
     * Die Zeilen, die in den Kindtabellen BLEIBEN duerfen. Alles andere des
     * Projekts gehoert zum Entwurf und wird geraeumt — auch Zeilen, die der
     * geladene Bericht nicht kennt (im Editor nur lokal geloescht). Der Prune
     * beim Speichern kann das nicht: eine leere Behalte-Liste laesst er
     * absichtlich stehen (Datenverlust-Guard vom 21.07.2026).
     */
    keepItemIds: string[];
    keepChecklistIds: string[];
    /** Eintraege des geladenen Entwurfs, die wegfallen — fuer den Dialog. */
    droppedItemIds: string[];
    /** Erklaerung fuer den Bestaetigungsdialog. */
    summary: string;
}

export interface DraftDiscardPlanBlocked {
    ok: false;
    reason: DraftDiscardBlockReason;
    message: string;
}

export type DraftDiscardPlan = DraftDiscardPlanOk | DraftDiscardPlanBlocked;

const formatDay = (ymd: string | undefined | null): string => {
    const d = parseDateLocal(ymd);
    if (!d) return 'unbekanntem Datum';
    return d.toLocaleDateString('de-DE');
};

const blocked = (reason: DraftDiscardBlockReason, message: string): DraftDiscardPlanBlocked => ({ ok: false, reason, message });

/**
 * Traegt das EIGENE Protokoll eine Buchung zu dieser Berichtsnummer?
 *
 * Nicht nur die Hauptbuchung (`isReportBooked`): auch eine Handbuchung aus
 * der Leistungserfassung („Begehung Bericht Nr. 5") und eine Zusatzleistung
 * („Zusatzleistung: Bericht #5") haengen an der Nummer — `reportNumberOf` ist
 * der kanonische Leser dafuer. Ausgenommen sind Buchungen der TOECHTER im
 * Protokoll einer Mutter (sie tragen die Nummer des Tochterberichts, nicht
 * der Mutter) und Abrechnungszeilen (BU-/ALT-/VZ-/RN-/MB-, keine Leistung).
 */
export const istEigeneBuchungZu = (log: ServiceTransaction[] | undefined, nr: number): boolean =>
    (log || []).some(e => !!e && !istKindBuchung(e) && !isAccountingEntry(e) && reportNumberOf(e) === nr);

/**
 * Hat die Tochter diese Berichtsnummer im Protokoll der MUTTER gebucht?
 * Beide Schreibweisen: `RPT-CHILD-<id>-<N>` und die Altform
 * `Tochter-Projekt "<Name>": Bericht Nr. <N>` (istBuchungDerTochter).
 */
export const istTochterBuchungZu = (
    parentLog: ServiceTransaction[] | undefined | null,
    childId: string,
    childName: string | null | undefined,
    nr: number,
): boolean =>
    (parentLog || []).some(e => !!e && istBuchungDerTochter(e, childId, childName) && reportNumberOf(e) === nr);

/**
 * Anwesenheiten des vorigen Berichts auf die HEUTIGE Kontaktliste legen.
 *
 * Die Liste selbst bleibt die lebende: Kontakte werden im Hub gepflegt und
 * liegen relational (project_contacts, mit Prune ueber die Behalte-Liste).
 * Die Liste aus dem Snapshot zu nehmen, wuerde seitdem angelegte Kontakte
 * beim naechsten Speichern loeschen. Nur das Haekchen „anwesend" gehoert zum
 * Bericht — und nur das kommt zurueck.
 */
export const restorePresence = (live: Contact[], snapshot: Contact[] | undefined): Contact[] => {
    const present = new Set((snapshot || []).filter(c => c?.present).map(c => c.id));
    return (live || []).map(c => {
        const soll = present.has(c.id);
        return c.present === soll ? c : { ...c, present: soll };
    });
};

/**
 * Abschnittszaehler zurueckdrehen — den Abschnitt, den das Anlegen wirklich
 * hochgezaehlt hat.
 *
 * `metadata.sectionId` taugt dafuer NICHT: „Entwurf bearbeiten" (Modus 'edit')
 * schreibt den gerade aktiven Abschnitt in den Bericht, ohne Zaehler
 * umzubuchen. Wer den Entwurf fuer Los A anlegt und ihn ueber Los B oeffnet,
 * traegt B im Bericht — hochgezaehlt wurde A. Erkannt wird der Abschnitt
 * deshalb am Zaehler: sein Stand liegt genau eins ueber dem Snapshot (ohne
 * Snapshot: ueber null). Passen mehrere, entscheidet der Bericht; passt
 * keiner eindeutig, bleiben die Abschnitte stehen — lieber ein Zaehler zu
 * hoch als der falsche zu niedrig.
 *
 * `lastDate` kommt aus dem Snapshot (Stand vor dem Anlegen); ohne Snapshot gab
 * es keine Begehung in dem Abschnitt, also auch kein Datum.
 */
export const revertSectionCounter = (
    live: ProjectSection[] | undefined,
    draftSectionId: string | undefined,
    snapshotSections: ProjectSection[] | undefined,
): ProjectSection[] | undefined => {
    if (!live || live.length === 0) return live;
    const vorher = new Map((snapshotSections || []).map(s => [s.id, s] as const));
    const standVorher = (id: string): number => vorher.get(id)?.currentInspectionNumber || 0;
    const kandidaten = live.filter(s => (s.currentInspectionNumber || 0) === standVorher(s.id) + 1);
    const ziel = kandidaten.length === 1 ? kandidaten[0] : kandidaten.find(s => s.id === draftSectionId);
    if (!ziel) return live;
    return live.map(s => (s.id !== ziel.id ? s : { ...s, currentInspectionNumber: standVorher(s.id), lastDate: vorher.get(s.id)?.lastDate }));
};

/**
 * Folgetermin nach dem Verwerfen.
 *
 * Der Turnus selbst (Typ, Wochentage) bleibt der lebende — der kann seit dem
 * Anlegen im Hub geaendert worden sein, und das Verwerfen eines Entwurfs ist
 * kein Grund, ihn zurueckzudrehen. Der TERMIN springt auf den Wert aus dem
 * Snapshot zurueck — mit derselben Regel, die der Speicherweg anwendet:
 * App.handleUpdateReport rechnet bei geaendertem Begehungsdatum den Termin
 * neu und nimmt den gerechneten, wenn keiner da ist, der alte nicht mehr nach
 * dem Begehungstag liegt oder der gerechnete spaeter faellt. Wer hier eine
 * andere Regel anwendete, schriebe in den Spalten-Vorgriff einen Termin, den
 * der Blob-Save gleich darauf ueberschreibt — und beim Laden gewinnt die
 * Spalte (loadProjectById).
 *
 * Ohne Snapshot (leere Huelle) bleibt der lebende Termin stehen: ein Projekt
 * VOR der ersten Begehung hat durchaus einen geplanten Ersttermin (Turnuswahl
 * im Hub, Verschieben im Dashboard), und der Anlagedialog kennt keinen Wert,
 * auf den man zurueckkoennte. SiFa hat keinen Turnus und bekommt nichts
 * angefasst.
 */
export const revertIntervalConfig = (
    liveMeta: Pick<ReportMetadata, 'projectType' | 'intervalConfig'>,
    restoredDate: string,
    snapshotConfig: IntervalConfig | undefined,
): IntervalConfig | undefined => {
    if (istSifaProjekt(liveMeta)) return liveMeta.intervalConfig;
    if (!parseDateLocal(restoredDate)) return liveMeta.intervalConfig;
    const current: IntervalConfig = liveMeta.intervalConfig || { type: 'weekly' };
    const vorher = snapshotConfig?.nextScheduledDate || '';
    const gerechnet = current.type === 'manual' ? '' : calculateNextDueDate(restoredDate, current);
    let next = vorher;
    if (gerechnet && (!next || next <= restoredDate || gerechnet > next)) next = gerechnet;
    if (next) return { ...current, nextScheduledDate: next };
    const rest: IntervalConfig = { ...current };
    delete rest.nextScheduledDate;
    return rest;
};

const isoOrNull = (value: string | undefined | null): string | null => (parseDateLocal(value) ? String(value).slice(0, 10) : null);

const ids = (rows: ReadonlyArray<{ id?: string } | null | undefined> | undefined): string[] =>
    (rows || []).map(r => r?.id).filter((id): id is string => !!id);

/**
 * Der Plan: entweder ein wiederhergestellter Bericht samt Spaltenwerten oder
 * ein benannter Grund, warum nicht. Rein, ohne Datenbank.
 */
export const planDraftDiscard = (live: Report, projectId: string, ev: DraftDiscardEvidence): DraftDiscardPlan => {
    const meta = live.metadata;
    const draftNr = meta.inspectionNumber || 0;

    if (draftNr < 1) return blocked('no_draft', 'Es gibt keinen Entwurf, der sich verwerfen liesse.');
    if (ev.lookupFailed) return blocked('lookup_failed', 'Die Pruefung gegen die Datenbank ist fehlgeschlagen. Der Entwurf wurde NICHT verworfen — bitte Verbindung pruefen und erneut versuchen.');

    const versandRiegel = ev.sendDecision?.alreadySent
        && ev.sendDecision.reason !== 'same_day'
        && ev.sendDecision.reason !== 'uebungsprojekt';
    if (meta.isExported || (ev.dbLastExportedNr ?? -1) >= draftNr || versandRiegel) {
        return blocked('exported', `Bericht #${draftNr} wurde bereits versendet — er ist kein Entwurf mehr und laesst sich nicht verwerfen.`);
    }
    if (ev.draftArchiveRows.some(r => r.inspectionNumber === draftNr)) {
        return blocked('archived', `Zu Bericht #${draftNr} liegt bereits ein Archiv-Eintrag vor — er ist kein Entwurf mehr.`);
    }
    if (istEigeneBuchungZu(meta.globalData?.serviceLog, draftNr)) {
        return blocked('booked', `Bericht #${draftNr} ist bereits im Leistungsprotokoll gebucht. Erst die Buchung zuruecknehmen, dann den Entwurf verwerfen.`);
    }
    const linkConfig = meta.globalData?.linkedProjectConfig;
    if (linkConfig?.role === 'child' && istTochterBuchungZu(ev.parentServiceLog, projectId, meta.projectName, draftNr)) {
        return blocked('booked_parent', `Bericht #${draftNr} ist im Mutterprojekt „${linkConfig.linkedProjectName}" gebucht. Erst die Buchung dort zuruecknehmen.`);
    }
    if (ev.freelancerRelease?.blockiert) {
        return blocked('freelancer', `Bericht #${draftNr} stammt aus einer Freelancer-Begehung, die noch nicht geprueft ist — die Eintraege sind dessen Feldarbeit. Erst pruefen oder zur Nachbesserung zurueckgeben (Freelancer → Pruefung).${ev.freelancerRelease.hinweis ? ` ${ev.freelancerRelease.hinweis}` : ''}`);
    }

    const previousNr = draftNr - 1;
    const snapshot = ev.previousSnapshot;
    if (previousNr >= 1 && ev.previousSnapshotAmbiguous) {
        return blocked('ambiguous_snapshot', `Zum vorigen Bericht #${previousNr} liegen mehrere Archiv-Staende (verschiedene Abschnitte) vor — welcher der Stand vor dem Anlegen war, laesst sich nicht sicher sagen. Der Entwurf wird nicht automatisch zurueckgenommen.`);
    }
    if (previousNr >= 1 && !snapshot) {
        return blocked('no_snapshot', `Vom vorigen Bericht #${previousNr} gibt es keinen Archiv-Stand. Der Entwurf laesst sich deshalb nicht automatisch zuruecknehmen.`);
    }

    const globalData = meta.globalData;
    const snapMeta = snapshot?.metadata;
    const restoredDate = snapshot ? (snapMeta?.date || '') : '';
    const restoredTime = snapshot ? (snapMeta?.time || meta.time || '08:00') : (meta.time || '08:00');
    const restoredItems: InspectionItem[] = snapshot ? (snapshot.items || []) : [];
    // Checklisten sind Berichtsinhalt (Status, Kommentar, Pruefdatum je
    // Eintrag) und werden nur im Editor gepflegt — sie kommen aus dem Snapshot.
    // Fehlt dort das Feld (aelterer Snapshot), bleibt der lebende Stand; die
    // leere Huelle hat keine (App.handleSetupComplete: `checklists: []`).
    const restoredChecklists = snapshot
        ? (Array.isArray(snapshot.checklists) ? snapshot.checklists : live.checklists)
        : [];
    const isExported = snapshot
        ? (snapMeta?.isExported ?? ((ev.dbLastExportedNr ?? 0) >= previousNr))
        : false;
    const intervalConfig = revertIntervalConfig(meta, restoredDate, snapMeta?.intervalConfig);

    const restoredMeta: ReportMetadata = {
        ...meta,
        inspectionNumber: previousNr,
        date: restoredDate,
        time: restoredTime,
        sectionId: snapshot ? snapMeta?.sectionId : undefined,
        isExported,
        currentBillingServiceId: snapshot ? snapMeta?.currentBillingServiceId : meta.currentBillingServiceId,
        currentBillingSplit: snapshot ? snapMeta?.currentBillingSplit : undefined,
        distributionList: restorePresence(meta.distributionList || [], snapMeta?.distributionList),
        guests: snapshot ? (snapMeta?.guests || []) : [],
        intervalConfig,
        globalData: globalData
            ? { ...globalData, sections: revertSectionCounter(globalData.sections, meta.sectionId, snapMeta?.globalData?.sections) }
            : globalData,
    };

    const restored: Report = {
        ...live,
        metadata: restoredMeta,
        items: restoredItems,
        checklists: restoredChecklists,
    };

    const keepItemIds = ids(restoredItems);
    const keep = new Set(keepItemIds);
    const droppedItemIds = ids(live.items).filter(id => !keep.has(id));

    const columns: DraftDiscardColumns = {
        current_inspection_number: previousNr,
        last_inspection_date: isoOrNull(restoredDate),
        next_scheduled_date: istSifaProjekt(meta) ? null : isoOrNull(intervalConfig?.nextScheduledDate),
    };

    const summary = snapshot
        ? `Bericht #${previousNr} vom ${formatDay(restoredDate)} wird wieder der aktuelle Bericht. Berichtszaehler, Begehungsdatum und Folgetermin springen auf den Stand von vorher zurueck.`
        : 'Das Projekt steht danach wieder vor der ersten Begehung: Berichtszaehler 0, kein Begehungsdatum, keine Eintraege und keine Checklisten.';

    return {
        ok: true, draftNr, previousNr, restored, columns, summary,
        keepItemIds,
        keepChecklistIds: ids(restoredChecklists),
        droppedItemIds,
    };
};
